<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Login V2"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <!-- form -->
          <validation-observer
            ref="loginForm"
            #default="{invalid}"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <!-- email -->
              <b-form-group
                label="Email"
                label-for="login-email"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    id="login-email"
                    v-model="userEmail"
                    :state="errors.length > 0 ? false:null"
                    name="login-email"
                    placeholder="john@example.com"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- forgot password -->
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Password</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  vid="password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Password"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-link
                :to="{ name: 'auth-forgot-password'}"
              >
                Forgot Password
              </b-link>

              <!-- submit buttons -->
              <b-button
                type="submit"
                variant="primary"
                class="mt-1"
                block
                :disabled="invalid"
              >
                Sign in
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BImg, BForm, BButton, VBTooltip,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BImg,
    BForm,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      password: 'mypassword',
      userEmail: 'name@company.com',
      sideImg: require('@/assets/images/logo/logo-smarthub.svg'),

      // validation rules
      required,
      email,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (this.$store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          useJwt.login({
            email: this.userEmail,
            password: this.password,
          })
            .then(tokenResponse => {
              const { refresh, access } = tokenResponse.data
              useJwt.setToken(access)
              useJwt.setRefreshToken(refresh)
              this.$http.get('/api/v1/users/me/')
                .then(response => {
                  const {
                    email: userEmail,
                    first_name: firstName,
                    last_name: lastName,
                    photo,
                    last_notifications_check: lastNotificationsCheck,
                    is_staff: isStaff,
                    is_superuser: isSuperuser,
                    groups,
                  } = response.data
                  const isManager = isStaff && isSuperuser
                  const role = groups.map(group => group.name).join(',')
                  let userData = {
                    userEmail,
                    firstName,
                    lastName,
                    photo,
                    lastNotificationsCheck,
                    role,
                  }
                  if (isManager || role.includes('platform-manager')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'all',
                        },
                      ],
                    }
                  } else if (role.includes('brand-manager')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'User',
                        },
                        {
                          action: 'manage',
                          subject: 'Manufacturer',
                        },
                        {
                          action: 'manage',
                          subject: 'BrandName',
                        },
                        {
                          action: 'manage',
                          subject: 'Collection',
                        },
                        {
                          action: 'manage',
                          subject: 'Agent',
                        },
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'manage',
                          subject: 'Settings',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else if (role.includes('brand')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else if (role.includes('manufacturer-manager')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'User',
                        },
                        {
                          action: 'manage',
                          subject: 'Brand',
                        },
                        {
                          action: 'manage',
                          subject: 'BrandName',
                        },
                        {
                          action: 'manage',
                          subject: 'Collection',
                        },
                        {
                          action: 'manage',
                          subject: 'Agent',
                        },
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'manage',
                          subject: 'Settings',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else if (role.includes('manufacturer')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else if (role.includes('agent-manager')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'User',
                        },
                        {
                          action: 'manage',
                          subject: 'Brand',
                        },
                        {
                          action: 'manage',
                          subject: 'BrandName',
                        },
                        {
                          action: 'manage',
                          subject: 'Collection',
                        },
                        {
                          action: 'manage',
                          subject: 'Manufacturer',
                        },
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'manage',
                          subject: 'Settings',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else if (role.includes('agent')) {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'manage',
                          subject: 'Style',
                        },
                        {
                          action: 'manage',
                          subject: 'Material',
                        },
                        {
                          action: 'manage',
                          subject: 'SampleRequest',
                        },
                        {
                          action: 'manage',
                          subject: 'Supplier',
                        },
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  } else {
                    userData = {
                      ...userData,
                      ability: [
                        {
                          action: 'view',
                          subject: 'Auth',
                        },
                      ],
                    }
                  }
                  localStorage.setItem('userData', JSON.stringify(userData))
                  this.$ability.update(userData.ability)

                  this.$store.commit('navBar/UPDATE_EMAIL', userEmail)
                  this.$store.commit('navBar/UPDATE_FIRST_NAME', firstName)
                  this.$store.commit('navBar/UPDATE_LAST_NAME', lastName)
                  this.$store.commit('navBar/UPDATE_PHOTO', photo)

                  // ? This is just for demo purpose. Don't think CASL is role based in this case, we used role in if condition just for ease
                  this.$router.replace(getHomeRouteForLoggedInUser('admin'))
                    .then(() => {
                      const fullName = this.$store.getters['navBar/fullName']
                      this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                          title: `Welcome ${fullName || this.$store.state.navBar.userEmail}`,
                          icon: 'CoffeeIcon',
                          variant: 'success',
                          text: `You have successfully logged in as ${userData.role}. Now you can start to explore!`,
                        },
                      })
                    })
                })
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Login error',
                  text: error.response.data.detail,
                  icon: 'CoffeIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
